import { BigNumber } from '@ethersproject/bignumber';
import { Constants } from '@manifoldxyz/js-ts-utils';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { RequiredFund, TxStepStatus } from '@/common/types';
import { getExtensionAddress } from '@/common/util';
import ClaimExtensionContract from '@/contracts/claimExtensionContract';
import { createApproveERC20Step } from '@/lib/transactionsFlow/steps/approveERC20';
import { createBuyStep } from '@/lib/transactionsFlow/steps/buy';
import { createConnectStep } from '@/lib/transactionsFlow/steps/connect';
import { createDelegateWalletStep } from '@/lib/transactionsFlow/steps/delegateWallet';
import { createMintStep } from '@/lib/transactionsFlow/steps/mint';
import { createReviewFundsStep } from '@/lib/transactionsFlow/steps/reviewFunds';
import { addStep } from '@/lib/transactionsFlow/utils';
import { StoreType } from '@/store/mainStore';

/**
 * Initializes the transaction flow for primary mints (direct from creator)
 * Sets up steps for wallet connection, allowlist validation, ERC20 approval, and minting
 * @param store - The global store instance
 * @param studioInstance - The studio instance containing mint configuration
 */
export async function initializePrimarySteps(
  store: StoreType,
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
): Promise<void> {
  // Always add connect step
  addStep(
    store,
    createConnectStep(studioInstance.publicData.network, undefined, {
      status: TxStepStatus.Pending,
    }),
  );
  // Add conditional steps based on configuration
  if (
    studioInstance.publicData.mintingRestriction === 'Allowlist' &&
    studioInstance.publicData.instanceAllowlist?.merkleTreeId
  ) {
    addStep(store, createDelegateWalletStep());
  }

  // Add ERC20 approval if needed
  if (studioInstance.publicData.mintPrice.erc20 !== Constants.NULL_ADDRESS) {
    const requiredFunds = await _getRequiredFunds(studioInstance, store.numberOfMints);
    if (requiredFunds.length > 0) {
      addStep(store, await createReviewFundsStep(requiredFunds[0]));
    }
    addStep(store, createApproveERC20Step(studioInstance));
  }

  // Add mint step
  addStep(store, createMintStep(studioInstance));
}

/**
 * Initializes the transaction flow for secondary market purchases
 * Sets up steps for wallet connection and token purchase from existing holders
 * @param store - The global store instance
 * @param studioInstance - The studio instance containing purchase configuration
 */
export async function initializeSecondarySteps(
  store: StoreType,
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
): Promise<void> {
  // Add connect step
  addStep(
    store,
    createConnectStep(studioInstance.publicData.network, undefined, {
      status: TxStepStatus.Pending,
    }),
  );

  // Add buy step
  addStep(store, createBuyStep(studioInstance));
}

/**
 * Calculates the required funds for a minting transaction
 * Handles both native currency (ETH) and ERC20 token requirements
 * @param studioInstance - The studio instance containing pricing information
 * @param numberOfMints - Number of tokens to mint
 * @returns Array of required fund specifications
 */
async function _getRequiredFunds(
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
  numberOfMints: number,
): Promise<RequiredFund[]> {
  const requiredFunds = [];
  const networkId = studioInstance.publicData.network;
  // Calculate total required amounts
  const mintPrice = BigNumber.from(studioInstance.publicData.mintPrice.value).mul(numberOfMints);

  // Get mint fee (always in native currency)
  const claimExtensionContract = new ClaimExtensionContract(
    studioInstance.publicData.network,
    getExtensionAddress(studioInstance),
    studioInstance.publicData.contract.contractAddress,
    parseInt(
      (studioInstance as ClaimStudioInstance).publicData?.claimIndex?.toString() ||
        studioInstance.id,
    ),
  );
  const mintFee = await claimExtensionContract.getMintFee();
  const totalMintFee = mintFee.mul(numberOfMints);

  // Check if mint price is in native currency or ERC20
  if (studioInstance.publicData.mintPrice.erc20 === Constants.NULL_ADDRESS) {
    // Both mint price and fee are in native currency
    const totalRequired = mintPrice.add(totalMintFee);
    requiredFunds.push({
      address: Constants.NULL_ADDRESS,
      amount: totalRequired,
      networkId,
      decimals: 18, // ETH decimals
    });

    return requiredFunds;
  }

  // Check ERC20 balance

  requiredFunds.push({
    address: studioInstance.publicData.mintPrice.erc20,
    amount: mintPrice,
    decimals: studioInstance.publicData.mintPrice.decimals,
    networkId,
  });

  // Check ETH balance for fees
  requiredFunds.push({
    address: Constants.NULL_ADDRESS,
    amount: totalMintFee,
    networkId,
    decimals: 18, // ETH decimals
  });

  return requiredFunds.filter((fund) => fund.address !== Constants.NULL_ADDRESS); // Only support ERC20s for now
}
